import React, { useState } from 'react';
import type { TrainingProgram } from '../types';

interface ProgramListProps {
  programs: TrainingProgram[];
  activeProgram: TrainingProgram | null;
  onRenameProgram: (programId: string, newName: string) => Promise<void>;
  onDeleteProgram: (programId: string) => Promise<void>;
  onSetActiveProgram: (programId: string) => Promise<void>;
}

interface ProgramCardProps {
  program: TrainingProgram;
  isActive: boolean;
  onRename: (newName: string) => Promise<void>;
  onDelete: () => Promise<void>;
  onSetActive: () => Promise<void>;
}

/**
 * ProgramCard - Individual program display with actions
 */
const ProgramCard: React.FC<ProgramCardProps> = ({
  program,
  isActive,
  onRename,
  onDelete,
  onSetActive
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(program.name);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSaveRename = async () => {
    if (editName.trim() && editName.trim() !== program.name) {
      try {
        setIsLoading(true);
        await onRename(editName.trim());
        setIsEditing(false);
      } catch (error) {
        // Error handling is done in parent component
      } finally {
        setIsLoading(false);
      }
    } else {
      setIsEditing(false);
      setEditName(program.name);
    }
  };

  const handleCancelRename = () => {
    setIsEditing(false);
    setEditName(program.name);
  };

  const handleDelete = async () => {
    try {
      setIsLoading(true);
      await onDelete();
      setShowDeleteConfirm(false);
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetActive = async () => {
    if (!isActive) {
      try {
        setIsLoading(true);
        await onSetActive();
      } catch (error) {
        // Error handling is done in parent component
      } finally {
        setIsLoading(false);
      }
    }
  };

  const totalExercises = program.workouts.reduce(
    (total, workout) => total + workout.exercises.length,
    0
  );

  return (
    <div className={`
      border rounded-lg p-4 transition-all
      ${isActive 
        ? 'border-green-300 bg-green-50' 
        : 'border-gray-200 bg-white hover:border-gray-300'
      }
    `}>
      {/* Program Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          {isEditing ? (
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveRename();
                  if (e.key === 'Escape') handleCancelRename();
                }}
                className="flex-1 px-2 py-1 text-lg font-semibold border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                maxLength={100}
                autoFocus
              />
              <button
                onClick={handleSaveRename}
                disabled={isLoading}
                className="text-green-600 hover:text-green-800 p-1"
                title="Сохранить"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </button>
              <button
                onClick={handleCancelRename}
                disabled={isLoading}
                className="text-gray-600 hover:text-gray-800 p-1"
                title="Отмена"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ) : (
            <div className="flex items-center">
              <h3 className="text-lg font-semibold">{program.name}</h3>
              {isActive && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                  Активная
                </span>
              )}
            </div>
          )}
        </div>

        {/* Action Menu */}
        {!isEditing && (
          <div className="flex items-center space-x-1 ml-4">
            {!isActive && (
              <button
                onClick={handleSetActive}
                disabled={isLoading}
                className="text-blue-600 hover:text-blue-800 p-1"
                title="Сделать активной"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            )}
            <button
              onClick={() => setIsEditing(true)}
              disabled={isLoading}
              className="text-gray-600 hover:text-gray-800 p-1"
              title="Переименовать"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isLoading}
              className="text-red-600 hover:text-red-800 p-1"
              title="Удалить"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Program Stats */}
      <div className="grid grid-cols-2 gap-4 mb-3 text-sm text-gray-600">
        <div>
          <span className="font-medium">{program.workouts.length}</span> тренировок
        </div>
        <div>
          <span className="font-medium">{totalExercises}</span> упражнений
        </div>
      </div>

      {/* Workouts Preview */}
      {program.workouts.length > 0 && (
        <div className="border-t border-gray-200 pt-3">
          <div className="space-y-3">
            {program.workouts.map((workout) => (
              <div key={workout.id} className="text-sm">
                <div className="font-medium text-gray-900 mb-1">
                  {workout.name}:
                </div>
                {workout.exercises.length > 0 ? (
                  <ul className="space-y-0.5 ml-2">
                    {workout.exercises.map((exercise, index) => (
                      <li key={exercise.id} className="text-gray-600">
                        - {exercise.exercise.name_ru}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-gray-400 ml-2 italic">
                    Нет упражнений
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Creation Date */}
      <div className="text-xs text-gray-500 mt-3">
        Создана: {new Date(program.created_at).toLocaleDateString('ru-RU')}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
            <h3 className="text-lg font-semibold mb-2">Удалить программу?</h3>
            <p className="text-gray-600 mb-4">
              Программа "{program.name}" будет удалена. История тренировок сохранится.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isLoading}
                className="btn-secondary"
              >
                Отмена
              </button>
              <button
                onClick={handleDelete}
                disabled={isLoading}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                {isLoading ? 'Удаление...' : 'Удалить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * ProgramList - List of training programs with management actions
 * Requirements: 4.1, 4.2
 */
export const ProgramList: React.FC<ProgramListProps> = ({
  programs,
  activeProgram,
  onRenameProgram,
  onDeleteProgram,
  onSetActiveProgram
}) => {
  if (programs.length === 0) {
    return null;
  }

  return (
    <div className="program-list space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">
          Программы тренировок ({programs.length})
        </h2>
      </div>

      <div className="space-y-4">
        {programs.map((program) => (
          <ProgramCard
            key={program.id}
            program={program}
            isActive={activeProgram?.id === program.id}
            onRename={(newName) => onRenameProgram(program.id, newName)}
            onDelete={() => onDeleteProgram(program.id)}
            onSetActive={() => onSetActiveProgram(program.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default ProgramList;